<app-loader [isBusy]="isBusy">
    <ng-template>
        <div class="chat-expand-container">
            <!-- Custom Header -->
            <div class="chat-expand-header">
                <div class="header-left">
                    <div class="header-title-section">
                        <fa-icon class="header-icon" [icon]="['fas', 'comments']"></fa-icon>
                        @if (!this.isBusy) {
                            <div class="title-content">
                                <h3 class="main-title">{{ getHeaderInfo().title }}</h3>
                                <div class="subtitle-info">
                                    <span class="sender-info">
                                        {{ 'From' | translate }}:
                                        <fa-icon class="info-icon" [icon]="['fas', 'user']"></fa-icon>
                                        {{ getHeaderInfo().sender }} ({{ getHeaderInfo().organization }})
                                    </span>
                                    @if (getHeaderInfo().date) {
                                        <span class="date-info">
                                            <fa-icon class="info-icon" [icon]="['fas', 'calendar']"></fa-icon>
                                            {{ getHeaderInfo().date | date: 'dd.MM.yyyy HH:mm' }}
                                        </span>
                                    }
                                    @if (rawData?.viewDate && flattenedData[0]?.createdBy?.id === this.thisUser?.id) {
                                        <span class="date-info ms-2">
                                            <fa-icon
                                                class="text-success"
                                                kendoTooltip
                                                [title]="
                                                    ('All recipients read on' | translate) +
                                                    ' ' +
                                                    (rawData.viewDate | date: 'dd.MM.yyyy HH:mm')
                                                "
                                                [icon]="['fas', 'check-double']"></fa-icon>
                                        </span>
                                    }
                                    @if (isNew) {
                                        <span class="status-info">
                                            <fa-icon
                                                class="text-primary"
                                                kendoTooltip
                                                size="2x"
                                                title="Unread Message"
                                                [icon]="['fas', 'envelope']"></fa-icon>
                                            {{ 'New' | translate }}
                                        </span>
                                    }
                                    @if (getHeaderInfo().hasMultipleNotes) {
                                        <span class="count-info">
                                            <fa-icon class="info-icon" [icon]="['fas', 'layer-group']"></fa-icon>
                                            {{ getHeaderInfo().noteCount }} {{ 'notes' | translate }}
                                        </span>
                                    }
                                </div>
                                @if (getHeaderInfo().forOrganization || getHeaderInfo().forUser) {
                                    <div class="subtitle-info">
                                        @if (getHeaderInfo().forOrganization) {
                                            <span class="recipient-org">
                                                {{ 'To' | translate }}:
                                                <fa-icon class="detail-icon" [icon]="['fas', 'building']"></fa-icon>
                                                {{ getHeaderInfo().forOrganization }}
                                            </span>
                                        }
                                        @if (getHeaderInfo().forUser) {
                                            <span class="recipient-user">
                                                <fa-icon class="detail-icon" [icon]="['fas', 'user']"></fa-icon>
                                                {{ getHeaderInfo().forUser }}
                                            </span>
                                        }
                                        <span class="type-info">
                                            <fa-icon class="info-icon" [icon]="['fas', 'tag']"></fa-icon>
                                            {{ getHeaderInfo().type }}
                                        </span>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </div>
                <div class="header-right">
                    <button
                        class="btn btn-outline-secondary btn-sm close-btn"
                        type="button"
                        [attr.aria-label]="'Close' | translate"
                        (click)="closeModalWindow()">
                        <fa-icon [icon]="['fas', 'times']"></fa-icon>
                    </button>
                </div>
            </div>

            <!-- Chat Messages Area -->
            <div class="chat-messages-area">
                @for (note of flattenedData; track note) {
                    <div
                        class="message-item"
                        [ngStyle]="getIndentationStyle(note.depth)"
                        [ngClass]="{ 'selected-for-reply': note.id === selectedReplyNote }"
                        (click)="selectReplyNote(note.id)">
                        <!-- Message Header -->
                        <div class="message-header">
                            <div class="message-sender">
                                <div class="user-avatar">
                                    {{
                                        getUserInitials(
                                            $safeNavigationMigration(note.createdBy?.firstName) +
                                                ' ' +
                                                $safeNavigationMigration(note.createdBy?.lastName)
                                        )
                                    }}
                                </div>
                                <div class="sender-details">
                                    <div class="sender-name">
                                        @if (note.depth > 0) {
                                            <fa-icon
                                                class="reply-icon text-primary mr-1"
                                                title="{{ 'Reply' | translate }}"
                                                [icon]="['fas', 'reply']"></fa-icon>
                                        }
                                        {{ note.createdBy?.displayName || 'Unknown User' }}
                                    </div>
                                    <div class="sender-org">{{ note.createdByOrganization?.name }}</div>
                                </div>
                            </div>
                            <div class="message-actions" kendoTooltip>
                                @if (selectedEditNote !== note.id) {
                                    <button
                                        class="btn btn-sm btn-primary"
                                        title="{{ 'Mark as Unread' | translate }}"
                                        [disabled]="
                                            note.isNew ||
                                            (note.createdBy.id === thisUser.id && note.chatNoteType.id !== 'INT_FOR_ME')
                                        "
                                        (click)="selectUnreadNote(note.id)">
                                        <fa-icon [icon]="['fas', 'envelope']"></fa-icon>
                                    </button>
                                }
                                @if (selectedEditNote !== note.id) {
                                    <button
                                        class="btn btn-sm btn-secondary"
                                        title="{{ 'Edit' | translate }}"
                                        [disabled]="
                                            (note.createdByOrganization.id !== thisUser.organization.id &&
                                                note.createdBy.id !== thisUser.id) ||
                                            !canEditChatNote(note.id)
                                        "
                                        (click)="selectEditNote(note.id)">
                                        <fa-icon [icon]="['fas', 'pen']"></fa-icon>
                                    </button>
                                }
                                @if (selectedEditNote === null) {
                                    <button
                                        class="btn btn-sm btn-danger"
                                        title="{{ 'Delete' | translate }}"
                                        [disabled]="
                                            (note.createdByOrganization.id !== thisUser.organization.id &&
                                                note.createdBy.id !== thisUser.id) ||
                                            !canRemoveChatNote(note.id)
                                        "
                                        (click)="selectRemoveNote(note.id)">
                                        <fa-icon [icon]="['fas', 'trash']"></fa-icon>
                                    </button>
                                }
                            </div>
                        </div>
                        <!-- Message Content -->
                        <div class="message-content">
                            <div class="small text-muted font-italic">
                                {{ note.createdDate | date: 'dd.MM.yyyy HH:mm' }}
                            </div>
                            @if (note.id !== selectedEditNote) {
                                <div class="message-text">{{ note.remark }}</div>
                            }
                            @if (note.id === selectedEditNote) {
                                <div class="message-edit">
                                    <input
                                        class="form-control"
                                        type="text"
                                        value="{{ note.remark }}"
                                        placeholder="{{ 'Enter note text' | translate }}"
                                        [(ngModel)]="editedNote" />
                                </div>
                            }
                            <!-- Attachment Display -->
                            @if (note.attachmentId && note.attachmentName && !editingAttachment) {
                                <div class="message-attachment">
                                    <div class="attachment-item">
                                        <fa-icon [icon]="['fas', 'paperclip']"></fa-icon>
                                        <span class="attachment-link" (click)="attachmentDownload(note.attachmentId)">
                                            {{ note.attachmentName }}
                                        </span>
                                        <fa-icon class="download-icon" [icon]="['fas', 'download']"></fa-icon>
                                    </div>
                                </div>
                            }
                            <!-- Edit Mode Fields -->
                            @if (note.id === selectedEditNote) {
                                <div class="edit-fields">
                                    <!-- Attachment Upload in Edit Mode -->
                                    @if (editingAttachment || !note.attachmentName) {
                                        <div class="attachment-upload">
                                            <label class="field-label">
                                                <fa-icon [icon]="['fas', 'paperclip']"></fa-icon>
                                                {{ 'Attachment' | translate }}
                                            </label>
                                            <kendo-fileselect
                                                [restrictions]="attachmentFileUploadRestrictions"
                                                [multiple]="false"
                                                (select)="fileSelected($event, false)"
                                                (remove)="fileRemoved($event, false)">
                                                <kendo-upload-messages
                                                    select="{{ 'Select File...' | translate }}"
                                                    dropFilesHere="{{
                                                        'Drop file here to select' | translate
                                                    }}"></kendo-upload-messages>
                                            </kendo-fileselect>
                                        </div>
                                    }
                                    <div class="edit-actions" kendoTooltip>
                                        @if (note.attachmentName !== null && !editingAttachment) {
                                            <button
                                                class="btn btn-sm btn-danger"
                                                title="{{ 'Remove Attachment' | translate }}"
                                                (click)="removeAttachmentOnEdit()">
                                                <fa-icon [icon]="['fas', 'trash']"></fa-icon>
                                            </button>
                                        }
                                        <button
                                            class="btn btn-sm btn-primary"
                                            title="{{ 'Save' | translate }}"
                                            (click)="saveEdit()">
                                            <fa-icon [icon]="['fas', 'check']"></fa-icon>
                                        </button>
                                        <button
                                            class="btn btn-sm btn-secondary"
                                            title="{{ 'Cancel' | translate }}"
                                            (click)="cancelEdit()">
                                            <fa-icon [icon]="['fas', 'times']"></fa-icon>
                                        </button>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                @if (
                    !isBusy &&
                    rawData !== null &&
                    (rawData.watcher === null || !userBelongsToOrganization(rawData.watcher.id))
                ) {
                    <div class="response-area">
                        <div class="row hidden">
                            <div class="col-4">
                                <label class="field-label">
                                    <fa-icon [icon]="['fas', 'tag']"></fa-icon>
                                    {{ 'Type' | translate }}
                                </label>
                                <kendo-combobox
                                    textField="name"
                                    valueField="id"
                                    [disabled]="true"
                                    [loading]="loadingChatTypes"
                                    [data]="chatTypes"
                                    [(ngModel)]="selectedChatType"
                                    (selectionChange)="chatTypeSelectionChange($event)"></kendo-combobox>
                            </div>
                            <div class="col-4">
                                <label class="field-label">
                                    <fa-icon [icon]="['fas', 'building']"></fa-icon>
                                    {{ 'For Organization' | translate }}
                                </label>
                                <kendo-combobox
                                    textField="name"
                                    valueField="id"
                                    [disabled]="true"
                                    [loading]="loadingChatTypes"
                                    [data]="orgSelection"
                                    [(ngModel)]="selectedOrg"
                                    (selectionChange)="organizationSelectionChange($event)"></kendo-combobox>
                            </div>
                            <div class="col-4">
                                <label class="field-label">
                                    <fa-icon [icon]="['fas', 'user']"></fa-icon>
                                    {{ 'For User' | translate }}
                                </label>
                                <kendo-combobox
                                    textField="displayName"
                                    valueField="id"
                                    [disabled]="true"
                                    [loading]="loadingChatTypes"
                                    [data]="userSelection"
                                    [(ngModel)]="selectedUser"></kendo-combobox>
                            </div>
                        </div>
                        <!-- Replying To Indicator -->
                        @if (selectedReplyNote) {
                            <div class="replying-to-indicator">
                                <fa-icon [icon]="['fas', 'reply']"></fa-icon>
                                <span>{{ 'Replying to' | translate }}: {{ getReplyToNoteInfo() }}</span>
                            </div>
                        }
                        <textarea
                            class="form-control response-textarea"
                            #responseTextarea
                            rows="1"
                            placeholder="{{ 'Select message above and type your response here...' | translate }}"
                            [(ngModel)]="newNote"
                            (keydown)="onTextareaKeydown($event)"></textarea>
                        <div class="response-actions align-items-center justify-content-between">
                            <kendo-fileselect
                                [restrictions]="attachmentFileUploadRestrictions"
                                [multiple]="false"
                                (select)="fileSelected($event, true)"
                                (remove)="fileRemoved($event, true)">
                                <kendo-upload-messages
                                    select="{{ 'Select File...' | translate }}"
                                    dropFilesHere="{{
                                        'Drop file here to select' | translate
                                    }}"></kendo-upload-messages>
                            </kendo-fileselect>
                            <div kendoTooltip>
                                <button
                                    class="btn btn-primary"
                                    title="{{ 'Send' | translate }}"
                                    [disabled]="!newNote && !fileToSend"
                                    (click)="submitReply()">
                                    <fa-icon [icon]="['fas', 'paper-plane']"></fa-icon>
                                </button>
                                <button
                                    class="btn btn-secondary ml-2"
                                    title="{{ 'Clear' | translate }}"
                                    [disabled]="!newNote && !fileToSend"
                                    (click)="clearNewNote()">
                                    <fa-icon [icon]="['fas', 'times']"></fa-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                }

                <!-- Delete Confirmation Dialog -->
                @if (openedModalConfirmDeleteNote) {
                    <kendo-dialog
                        title="{{ 'Remove Note' | translate }}"
                        [minWidth]="450"
                        [maxHeight]="800"
                        (close)="closeRemoveNote()">
                        <div class="confirmation-content">
                            <fa-icon class="warning-icon" [icon]="['fas', 'exclamation-triangle']"></fa-icon>
                            {{ 'Are you sure you want to delete this note?' | translate }}
                        </div>
                        <div class="confirmation-actions">
                            <button class="btn btn-sm btn-secondary" (click)="closeRemoveNote()">
                                <fa-icon [icon]="['fas', 'times']"></fa-icon>
                                {{ 'Cancel' | translate }}
                            </button>
                            <button class="btn btn-sm btn-danger" (click)="confirmRemoveNote()">
                                <fa-icon [icon]="['fas', 'trash']"></fa-icon>
                                {{ 'Delete' | translate }}
                            </button>
                        </div>
                    </kendo-dialog>
                }
            </div>
        </div>
    </ng-template>
</app-loader>
